/**
 * Document resubmission page.
 *
 * Displayed when a case has been returned with a resubmission request.
 * The applicant uploads replacement documents and re-submits.
 *
 * @module pages/ResubmissionPage
 */

import React, { useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient, uploadFileToSignedUrl } from '../lib/api';
import { FileUpload } from '../components/FileUpload';
import { Footer } from '../components/Footer';

interface ResubmissionPageProps {
  context: ApplicantContext;
}

type UploadStatus = 'idle' | 'uploading' | 'confirming' | 'success' | 'error';

export function ResubmissionPage({ context }: ResubmissionPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');

  const caseId = searchParams.get('caseId');
  const submissionSetId = searchParams.get('ss');

  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const [, setUploadId] = useState<string | null>(null);

  const handleUpload = useCallback(async () => {
    if (!file || !caseId) return;

    setStatus('uploading');
    setError(null);
    setUploadId(null);

    let currentUploadId: string | null = null;

    try {
      const client = getApiClient();

      // If no submission set was provided, create one.
      let ssId = submissionSetId;
      if (!ssId) {
        const ssResponse = await client.applicant.createSubmissionSet(caseId);
        ssId = ssResponse.data.id;
      }

      // Step 1: Request upload URL and obtain uploadId.
      const uploadResponse = await client.applicant.requestUploadUrl(ssId, {
        evidenceType: 'other',
        mimeType: file.type,
        fileName: file.name,
      });

      currentUploadId = uploadResponse.data.uploadId;
      setUploadId(currentUploadId);

      // Step 2: PUT the file to the signed URL.
      await uploadFileToSignedUrl(uploadResponse.data.signedUrl, file);

      // Step 3: Confirm the upload is complete.
      setStatus('confirming');
      await client.applicant.confirmUpload(currentUploadId, {
        fileSizeBytes: file.size,
      });

      setStatus('success');
    } catch (err: unknown) {
      setStatus('error');
      const apiErr = err as ApiClientError;
      const supportRef = currentUploadId ? ` (ref: ${currentUploadId})` : '';
      setError(
        (apiErr.message ?? t('errors.uploadFailed')) + supportRef,
      );
    }
  }, [file, caseId, submissionSetId, t]);

  // Missing case ID -- show error state.
  if (!caseId) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <header className="border-b border-hairline bg-surface">
          <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-4">
            {context.branding.logoUrl && (
              <img
                src={context.branding.logoUrl}
                alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
                className="h-8"
              />
            )}
            <h1
              className="text-lg font-semibold"
              style={{ color: 'var(--nv-primary-color)' }}
            >
              {t('resubmission.headerTitle')}
            </h1>
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center px-4">
          <div
            className="max-w-md rounded-lg border border-hairline bg-surface-raised p-8 text-center"
            role="alert"
          >
            <h2 className="mb-2 text-xl font-bold text-ink">
              {t('resubmission.missingTitle')}
            </h2>
            <p className="mb-4 text-sm text-ink-muted">
              {t('resubmission.missingDescription')}
            </p>
            <a
              href="/"
              className="inline-block rounded-md px-6 py-2 text-sm font-semibold text-white transition-opacity hover:brightness-90"
              style={{ backgroundColor: 'var(--nv-primary-color)' }}
            >
              {t('app.goToStart')}
            </a>
          </div>
        </main>
        <Footer branding={context.branding} />
      </div>
    );
  }

  // Success state.
  if (status === 'success') {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <header className="border-b border-hairline bg-surface">
          <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-4">
            {context.branding.logoUrl && (
              <img
                src={context.branding.logoUrl}
                alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
                className="h-8"
              />
            )}
            <h1
              className="text-lg font-semibold"
              style={{ color: 'var(--nv-primary-color)' }}
            >
              {t('resubmission.successHeaderTitle')}
            </h1>
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center px-4">
          <div className="max-w-md rounded-lg border border-hairline bg-surface-raised p-8 text-center">
            <div
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: 'var(--nv-primary-color)' }}
            >
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-bold text-ink">
              {t('resubmission.successTitle')}
            </h2>
            <p className="mb-2 text-sm text-ink-muted">
              {t('resubmission.successDescription')}
            </p>
            <p className="mb-6 text-xs text-ink-muted">
              {t('resubmission.caseRefLabel')} <span className="font-mono">{caseId}</span>
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-lg border border-hairline-strong px-6 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-sunken"
            >
              {t('resubmission.returnToStart')}
            </button>
          </div>
        </main>
        <Footer branding={context.branding} />
      </div>
    );
  }

  // Upload form.
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('resubmission.headerTitle')}
          </h1>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="rounded-lg border border-hairline bg-surface-raised p-8">
            <h2 className="mb-2 text-center text-xl font-bold text-ink">
              {t('resubmission.title')}
            </h2>
            <p className="mb-2 text-center text-sm text-ink-muted">
              {t('resubmission.description')}
            </p>
            <p className="mb-6 text-center text-xs text-ink-muted">
              {t('resubmission.caseLabel')} <span className="font-mono">{caseId}</span>
            </p>

            <div className="mb-6">
              <FileUpload
                id="resubmission-file"
                label={t('resubmission.uploadLabel')}
                onFileChange={setFile}
                file={file}
                disabled={status === 'uploading' || status === 'confirming'}
              />
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || status === 'uploading' || status === 'confirming'}
              className="flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: 'var(--nv-primary-color)',
                '--tw-ring-color': 'var(--nv-primary-color)',
              } as React.CSSProperties}
            >
              {(status === 'uploading' || status === 'confirming') && (
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden="true"
                />
              )}
              {status === 'uploading'
                ? t('resubmission.uploading')
                : status === 'confirming'
                  ? t('resubmission.confirming')
                  : t('resubmission.uploadButton')}
            </button>
          </div>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
